import React, { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Loader2, Network, ZoomIn, ZoomOut, RotateCcw, Package, icons } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AssetNode, AssetEdge } from '@/types/asset-relationship';
import { RELATIONSHIP_TYPES, IMPORTANCE_LEVELS } from '@/types/asset-relationship';

interface AssetRelationshipGraphProps {
  graphData?: { nodes: AssetNode[]; edges: AssetEdge[] };
  isLoading: boolean;
  centerAssetId: string;
}

interface GraphNode extends AssetNode {
  x: number;
  y: number;
  isDragging?: boolean;
}

const resolveIcon = (iconName?: string) => {
  if (!iconName) return Package;
  // Try PascalCase first (e.g. "Laptop", "Server")
  const Icon = (icons as Record<string, any>)[iconName];
  if (Icon) return Icon;
  // Try converting kebab-case to PascalCase
  const pascal = iconName
    .split('-')
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join('');
  return (icons as Record<string, any>)[pascal] || Package;
};

export const AssetRelationshipGraph = ({ 
  graphData, 
  isLoading, 
  centerAssetId 
}: AssetRelationshipGraphProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [nodes, setNodes] = useState<GraphNode[]>([]);
  const [edges, setEdges] = useState<AssetEdge[]>([]);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [draggedNode, setDraggedNode] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const SVG_WIDTH = 600;
  const SVG_HEIGHT = 400;
  const NODE_RADIUS = 32;

  const calculateNodePositions = (nodes: AssetNode[]): GraphNode[] => {
    if (nodes.length === 0) return [];
    const centerX = SVG_WIDTH / 2;
    const centerY = SVG_HEIGHT / 2;
    const radius = Math.min(SVG_WIDTH, SVG_HEIGHT) / 3;
    const centerNode = nodes.find(node => node.id === centerAssetId);
    const positionedNodes: GraphNode[] = [];

    if (centerNode) {
      positionedNodes.push({ ...centerNode, x: centerX, y: centerY });
      const otherNodes = nodes.filter(node => node.id !== centerAssetId);
      const angleStep = (2 * Math.PI) / Math.max(otherNodes.length, 1);
      otherNodes.forEach((node, index) => {
        positionedNodes.push({
          ...node,
          x: centerX + radius * Math.cos(index * angleStep),
          y: centerY + radius * Math.sin(index * angleStep),
        });
      });
    } else {
      nodes.forEach((node, index) => {
        const angle = (index * 2 * Math.PI) / nodes.length;
        positionedNodes.push({
          ...node,
          x: centerX + radius * Math.cos(angle),
          y: centerY + radius * Math.sin(angle),
        });
      });
    }
    return positionedNodes;
  };

  useEffect(() => {
    if (graphData?.nodes && graphData?.edges) {
      setNodes(calculateNodePositions(graphData.nodes));
      setEdges(graphData.edges);
    }
  }, [graphData, centerAssetId]);

  const handleZoomIn = () => setZoom(prev => Math.min(prev * 1.2, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev / 1.2, 0.3));
  const handleReset = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const handleMouseDown = (nodeId: string, event: React.MouseEvent) => {
    event.preventDefault();
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    setDraggedNode(nodeId);
    setDragOffset({
      x: (event.clientX - rect.left - pan.x) / zoom - node.x,
      y: (event.clientY - rect.top - pan.y) / zoom - node.y,
    });
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!draggedNode) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mouseX = (event.clientX - rect.left - pan.x) / zoom;
    const mouseY = (event.clientY - rect.top - pan.y) / zoom;
    setNodes(prev => prev.map(node => 
      node.id === draggedNode
        ? { ...node, x: mouseX - dragOffset.x, y: mouseY - dragOffset.y }
        : node
    ));
  };

  const handleMouseUp = () => {
    setDraggedNode(null);
    setDragOffset({ x: 0, y: 0 });
  };

  const getNodeColor = (node: GraphNode) => {
    if (node.id === centerAssetId) return '#3b82f6';
    const importance = IMPORTANCE_LEVELS[node.importance];
    switch (importance?.color) {
      case 'red': return '#ef4444';
      case 'orange': return '#f97316';
      case 'yellow': return '#eab308';
      case 'green': return '#22c55e';
      default: return '#6b7280';
    }
  };

  const getNodeGradientId = (node: GraphNode) => `grad-${node.id.slice(0, 8)}`;

  const getEdgeColor = (edge: AssetEdge) => {
    const importance = IMPORTANCE_LEVELS[edge.importance];
    switch (importance?.color) {
      case 'red': return '#ef4444';
      case 'orange': return '#f97316';
      case 'yellow': return '#eab308';
      case 'green': return '#22c55e';
      default: return '#6b7280';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin mr-3" />
        <span>Carregando grafo de relacionamentos...</span>
      </div>
    );
  }

  if (!graphData || nodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-muted-foreground">
        <Network className="h-16 w-16 mb-4" />
        <h4 className="text-lg font-medium mb-2">Nenhum relacionamento para visualizar</h4>
        <p className="text-center">
          Adicione relacionamentos para ver o mapa de dependências deste ativo.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controles */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="secondary">
            {nodes.length} ativo{nodes.length !== 1 ? 's' : ''}
          </Badge>
          <Badge variant="secondary">
            {edges.length} relacionamento{edges.length !== 1 ? 's' : ''}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleZoomOut}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground min-w-[60px] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <Button variant="outline" size="sm" onClick={handleZoomIn}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleReset}>
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* SVG Graph */}
      <Card className="p-4">
        <div className="w-full overflow-hidden border rounded-lg bg-muted/30">
          <svg
            ref={svgRef}
            width="100%"
            height={SVG_HEIGHT}
            viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
            className="cursor-move max-w-full"
            preserveAspectRatio="xMidYMid meet"
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <defs>
              {/* Drop shadow filter */}
              <filter id="node-shadow" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000" floodOpacity="0.2" />
              </filter>
              {/* Arrow marker */}
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#374151" />
              </marker>
              {/* Gradients for each node */}
              {nodes.map((node) => {
                const baseColor = getNodeColor(node);
                return (
                  <linearGradient key={getNodeGradientId(node)} id={getNodeGradientId(node)} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={baseColor} stopOpacity="1" />
                    <stop offset="100%" stopColor={baseColor} stopOpacity="0.75" />
                  </linearGradient>
                );
              })}
            </defs>

            <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
              {/* Edges */}
              {edges.map((edge) => {
                const sourceNode = nodes.find(n => n.id === edge.source);
                const targetNode = nodes.find(n => n.id === edge.target);
                if (!sourceNode || !targetNode) return null;
                const relationshipType = RELATIONSHIP_TYPES[edge.type];
                return (
                  <g key={edge.id}>
                    <line
                      x1={sourceNode.x}
                      y1={sourceNode.y}
                      x2={targetNode.x}
                      y2={targetNode.y}
                      stroke={getEdgeColor(edge)}
                      strokeWidth="2"
                      markerEnd="url(#arrowhead)"
                    />
                    <text
                      x={(sourceNode.x + targetNode.x) / 2}
                      y={(sourceNode.y + targetNode.y) / 2 - 8}
                      textAnchor="middle"
                      className="fill-muted-foreground"
                      style={{ fontSize: '10px' }}
                    >
                      {relationshipType?.label || edge.type}
                    </text>
                  </g>
                );
              })}

              {/* Nodes */}
              {nodes.map((node) => {
                const IconComponent = resolveIcon(node.icon);
                const isCenter = node.id === centerAssetId;
                return (
                  <g key={node.id} filter="url(#node-shadow)">
                    {/* Circle */}
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={NODE_RADIUS}
                      fill={`url(#${getNodeGradientId(node)})`}
                      stroke={isCenter ? '#1d4ed8' : 'rgba(255,255,255,0.3)'}
                      strokeWidth={isCenter ? 3 : 1.5}
                      className="cursor-pointer transition-opacity hover:opacity-90"
                      onMouseDown={(e) => handleMouseDown(node.id, e)}
                    />
                    {/* Icon via foreignObject */}
                    <foreignObject
                      x={node.x - 12}
                      y={node.y - 12}
                      width={24}
                      height={24}
                      className="pointer-events-none"
                    >
                      <div className="flex items-center justify-center w-full h-full">
                        <IconComponent size={20} color="white" strokeWidth={1.8} />
                      </div>
                    </foreignObject>
                    {/* Name below */}
                    <text
                      x={node.x}
                      y={node.y + NODE_RADIUS + 14}
                      textAnchor="middle"
                      className="fill-foreground font-medium"
                      style={{ fontSize: '11px' }}
                    >
                      {node.name.length > 14 ? `${node.name.substring(0, 14)}…` : node.name}
                    </text>
                    {/* Type label */}
                    {node.type && !/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(node.type) && node.type !== 'Ativo' && (
                      <text
                        x={node.x}
                        y={node.y + NODE_RADIUS + 26}
                        textAnchor="middle"
                        className="fill-muted-foreground"
                        style={{ fontSize: '9px' }}
                      >
                        {node.type}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
      </Card>

      {/* Legend */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <h5 className="font-medium mb-2">Níveis de Importância:</h5>
          <div className="space-y-1">
            {Object.entries(IMPORTANCE_LEVELS).map(([key, level]) => (
              <div key={key} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ 
                    backgroundColor: 
                      level.color === 'red' ? '#ef4444' :
                      level.color === 'orange' ? '#f97316' :
                      level.color === 'yellow' ? '#eab308' :
                      level.color === 'green' ? '#22c55e' : '#6b7280'
                  }}
                />
                <span>{level.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h5 className="font-medium mb-2">Controles:</h5>
          <div className="space-y-1 text-muted-foreground">
            <div>• Arraste os nós para reposicionar</div>
            <div>• Use os botões de zoom para ajustar</div>
            <div>• O nó azul representa o ativo atual</div>
            <div>• Ícones representam o tipo do ativo</div>
          </div>
        </div>
      </div>
    </div>
  );
};
